import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import PrestigeBadge from '../PrestigeBadge';
import { hasFeatureAccess, shouldShowPreview, getRequiredTier, getTierDisplayName } from '../../config/featureAccess';
import { useSubscription } from '../../hooks/useSubscription';
import { usePrefetchNav } from '../../hooks/usePrefetchNav';
const ADMIN_EMAIL_ALLOWLIST = ['admin@pitchlab.com', 'admin@earnmyedge.com'];

const NavigationBar = ({ items = [], moreItems = null, hideMore = false, homePath = '/app/dashboard', hideLocks = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, signOut } = useAuth();
  const { features, tenantSlug } = useTenant();
  const { toggleTheme } = useTheme();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const moreMenuRef = useRef(null);

  // Get user's subscription tier (default to 'free' if not set)
  const { tier, ugcTier } = useSubscription();
  const userTier = tier || profile?.subscription_tier || 'free';
  const hasCoachingPlan = userTier && userTier !== 'free';
  const hasUGCPlan = ugcTier && ugcTier !== 'free';

  // Prefetch nav routes on hover
  const { prefetchPath } = usePrefetchNav();

  // Default navigation items if none provided (keep minimal for mobile-first)
  const defaultNavItems = [
    { path: '/app/dashboard', label: 'Home', icon: 'Home' },
    { path: '/app/daily-drill', label: 'Practice', icon: 'Target' },
    { path: '/app/objection-dojo', label: 'Objections', icon: 'Shield', isPremium: true },
    { path: '/app/mega-trainer', label: 'Mega Trainer', icon: 'BookOpen' },
  ];

  // Simplified nav items for white-label tenants
  const tenantNavItems = [
    { path: '/app/dashboard', label: 'Home', icon: 'Home' },
    { path: '/app/daily-drill', label: 'Practice', icon: 'Target' },
    { path: '/app/objection-dojo', label: 'Objections', icon: 'Shield', isPremium: true },
    { path: '/app/ai-roleplay', label: 'Roleplay', icon: 'Users', isPremium: true },
  ];

  const defaultMoreNavItems = [
    { path: '/app/ai-roleplay', label: 'AI Roleplay', icon: 'Users', isPremium: true },
    { path: '/app/upload-center', label: 'Upload Center', icon: 'Upload' },
    { path: '/app/gamification', label: 'Achievements', icon: 'Zap' },
    { path: '/app/conversation-analyzer', label: 'Analyzer', icon: 'MessageSquare' },
    { path: '/app/script-lab', label: 'Scripts', icon: 'FileText' },
    { path: '/app/lumo-progress', label: 'Lumo Progress', icon: 'TrendingUp', isPremium: true },
    { path: '/app/cascade-challenge', label: 'Cascade Challenge', icon: 'Zap' },
    { path: '/app/activity-report', label: 'Activity', icon: 'BarChart' },
  ];

  // More items for white-label tenants (minimal set)
  const tenantMoreItems = [
    { path: '/app/upload-center', label: 'Upload Center', icon: 'Upload' },
    { path: '/app/script-lab', label: 'Scripts', icon: 'FileText' },
    { path: '/app/conversation-analyzer', label: 'Analyzer', icon: 'MessageSquare' },
    { path: '/app/activity-report', label: 'Activity', icon: 'BarChart' },
  ];

  // Append Admin for allowlisted users
  const isAdminUser = !!(user?.email && ADMIN_EMAIL_ALLOWLIST.some(e => e.toLowerCase() === user.email.toLowerCase()));
  if (isAdminUser) {
    defaultMoreNavItems.push({ path: '/app/admin', label: 'Admin', icon: 'ShieldCheck' });
  }

  const baseMoreNavItems = hideMore
    ? []
    : (moreItems || defaultMoreNavItems);

  // Filter nav items based on feature access AND tenant features
  const getFilteredNavItems = (items) => {
    return items.filter(item => {
      // First check subscription-based access
      const hasAccess = hasFeatureAccess(item.path, userTier);
      const showPreview = shouldShowPreview(item.path);
      
      // Then check tenant feature gating (only for non-default tenants)
      let isTenantFeatureEnabled = true;
      if (features && tenantSlug !== 'default') {
        // Map feature paths to feature flags (only restrict for automotive/insurance)
        const featureMap = {
          '/app/daily-drill': 'enableObjectionDrills',
          '/app/objection-dojo': 'enableObjectionDrills',
          '/app/script-lab': 'enableScripts',
          '/app/ai-roleplay': 'enableRoleplay',
          '/app/conversation-analyzer': 'enableConversationAnalyzer',
          '/app/gamification': 'enableGamification',
          '/app/cascade-challenge': 'enableGamification',
          '/app/leaderboards': 'enableLeaderboards',
          '/app/mega-trainer': 'enableMegaTrainer',
          '/app/lumo-progress': 'enableLiveCoaching',
          '/app/live-call': 'enableLiveCoaching',
          '/app/phone-sequence': 'enablePhoneSequences',
          '/app/micro-lessons': 'enableMicroLessons',
          '/app/ugc': 'enableUGC',
          '/app/ugc-hub': 'enableUGC'
        };
        
        const requiredFeature = featureMap[item.path];
        if (requiredFeature && !features[requiredFeature]) {
          isTenantFeatureEnabled = false;
        }
      }
      
      // Show if user has access OR if it should show preview, AND tenant feature is enabled
      return (hasAccess || showPreview) && isTenantFeatureEnabled;
    });
  };

  // Use tenant-specific nav items if on white-label tenant
  const isWhiteLabelTenant = tenantSlug && tenantSlug !== 'default';
  const primaryNavItems = isWhiteLabelTenant ? tenantNavItems : defaultNavItems;
  const effectiveMoreNavItems = isWhiteLabelTenant ? tenantMoreItems : baseMoreNavItems;

  const navItems = (items?.length > 0 ? items : getFilteredNavItems(primaryNavItems))
    .filter(item => item.path !== location?.pathname);
  const filteredMoreNavItems = getFilteredNavItems(effectiveMoreNavItems);
  const mobileMenuItems = hideMore ? [...navItems] : [...navItems, ...effectiveMoreNavItems];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef?.current && !profileMenuRef?.current?.contains(event?.target)) {
        setIsProfileOpen(false);
      }
      if (moreMenuRef?.current && !moreMenuRef?.current?.contains(event?.target)) {
        setIsMoreOpen(false);
      }
    };

    document?.addEventListener('mousedown', handleClickOutside);
    return () => document?.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build tenant query param for all navigation calls
  const tenantQuery = tenantSlug && tenantSlug !== 'default' ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/authentication');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };
  // Detect if on UGC pages for branding
  const isUGCPage = location?.pathname.includes('/app/ugc') || location?.pathname.includes('/teleprompter');
  const logoTitle = isUGCPage ? 'Earn My Edge' : 'Earn My Edge';
  const logoSubtitle = isUGCPage ? 'UGC Creator Platform' : null;
  const handleSettingsClick = () => {
    // Detect if we're in UGC product (any path starting with /app/ugc)
    const isUGCProduct = location?.pathname?.includes('/ugc');
    const settingsPath = isUGCProduct ? '/app/ugc-settings' : '/app/settings';
    navigate(`${settingsPath}${tenantQuery}`);
    setIsProfileOpen(false);
  };

  const handleProductSwitch = () => {
    navigate(`/app/dashboard${tenantQuery}`);
    setIsProfileOpen(false);
  };

  const handleNavClick = (item) => {
    const hasAccess = hasFeatureAccess(item.path, userTier);
    
    if (hasAccess) {
      navigate(`${item.path}${tenantQuery}`);
    } else {
      // Navigate to the feature page which will show the premium preview
      navigate(`${item.path}${tenantQuery}`);
    }
  };

  return (
    <nav className="bg-card border-b border-border sticky top-0 z-50">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center h-16 gap-4 md:gap-8">
          {/* Logo + mobile menu */}
          <div className="flex items-center gap-2">
            <button
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-border bg-background hover:bg-muted"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Open menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={18} />
            </button>
            <button 
              onClick={() => navigate(`${homePath || '/app/dashboard'}${tenantQuery}`)}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity flex-shrink-0"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-sm ${isUGCPage ? 'bg-gradient-to-br from-amber-400 to-orange-500' : 'bg-gradient-to-br from-blue-500 to-purple-600'}`}>
                <Icon name="Sparkles" size={20} color="white" />
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-bold text-foreground leading-tight">{logoTitle}</p>
                {logoSubtitle && <p className="text-xs text-muted-foreground leading-tight">{logoSubtitle}</p>}
              </div>
            </button>
          </div>

          {/* Navigation Items - Center with flex-1 to push edges */}
          <div className="hidden md:flex items-center gap-1 flex-1 justify-center">
            {navItems?.map((item, index) => {
              const isActive = location?.pathname === item?.path;
              const hasAccess = hasFeatureAccess(item.path, userTier);
              const isLocked = hideLocks ? false : !hasAccess;
              const requiredTier = getRequiredTier(item.path);
              
              return (
                <button
                  key={index}
                  onClick={() => handleNavClick(item)}
                  onMouseEnter={() => prefetchPath(item.path)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-all relative
                    ${isActive 
                      ? 'bg-primary/10 text-primary' 
                      : isLocked
                        ? 'text-muted-foreground hover:bg-amber-50 hover:text-amber-700'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }
                  `}
                >
                  <div className="flex items-center gap-2">
                    {!hideLocks && isLocked && <Icon name="Lock" size={14} className="text-amber-500" />}
                    {item?.icon && <Icon name={item?.icon} size={18} />}
                    {item?.label}
                    {!hideLocks && isLocked && (
                      <span className="ml-1 px-1.5 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs rounded font-bold">
                        {getTierDisplayName(requiredTier)}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}

            {/* More Menu */}
            {!hideMore && (
              <div ref={moreMenuRef} className="relative">
                <button
                  onClick={() => setIsMoreOpen(!isMoreOpen)}
                  onMouseEnter={() => {
                    // Prefetch all more items on hover
                    filteredMoreNavItems.forEach(item => prefetchPath(item.path));
                  }}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-all
                    ${isMoreOpen || effectiveMoreNavItems.some(item => location?.pathname === item?.path)
                      ? 'bg-primary/10 text-primary' 
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }
                  `}
                >
                  <div className="flex items-center gap-2">
                    <Icon name="MoreHorizontal" size={18} />
                    More
                    <Icon 
                      name={isMoreOpen ? 'ChevronUp' : 'ChevronDown'} 
                      size={16} 
                    />
                  </div>
                </button>

                {/* More Dropdown */}
                {isMoreOpen && (
                  <div className="absolute left-0 mt-2 w-64 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50">
                    {filteredMoreNavItems?.map((item, index) => {
                      const isActive = location?.pathname === item?.path;
                      const hasAccess = hasFeatureAccess(item.path, userTier);
                      const isLocked = hideLocks ? false : !hasAccess;
                      const requiredTier = getRequiredTier(item.path);
                      
                      return (
                        <button
                          key={index}
                          onClick={() => {
                            handleNavClick(item);
                            setIsMoreOpen(false);
                          }}
                          onMouseEnter={() => prefetchPath(item.path)}
                          className={`
                            w-full px-4 py-3 text-sm font-medium transition-colors flex items-center gap-3
                            ${isActive
                              ? 'bg-primary/10 text-primary'
                              : isLocked
                                ? 'text-foreground bg-amber-50/50 hover:bg-amber-100/50'
                                : 'text-foreground hover:bg-accent'
                            }
                          `}
                        >
                          {!hideLocks && isLocked && <Icon name="Lock" size={14} className="text-amber-500" />}
                          <Icon name={item?.icon} size={16} />
                          <span className="flex-1 text-left">{item?.label}</span>
                          {!hideLocks && isLocked && (
                            <span className="px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs rounded font-bold">
                              {getTierDisplayName(requiredTier)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Profile Dropdown - Far Right */}
          {user && (
            <div className="flex items-center gap-4 ml-auto">
              {/* Profile Menu */}
              <div ref={profileMenuRef} className="relative flex-shrink-0">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <Icon name="User" size={18} color="var(--color-primary)" />
                  <span className="text-sm font-medium text-foreground">
                    {profile?.full_name?.split(' ')?.[0] || user?.email?.split('@')?.[0]}
                  </span>
                  <Icon 
                    name={isProfileOpen ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                    color="var(--color-muted-foreground)"
                  />
                </button>

                {/* Dropdown Menu */}
                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
                    {/* Profile Info */}
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-semibold text-foreground">
                        {profile?.full_name || user?.email}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {user?.email}
                      </p>
                      <div className="mt-2 flex flex-col gap-1">
                        {hasCoachingPlan && (
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-primary/10 text-primary">
                              {getTierDisplayName(userTier)}
                            </span>
                            <span className="text-[11px] text-muted-foreground">Coaching</span>
                          </div>
                        )}
                        {hasUGCPlan && (
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                              {getTierDisplayName(ugcTier)}
                            </span>
                            <span className="text-[11px] text-muted-foreground">UGC</span>
                          </div>
                        )}
                      </div>
                      {/* Only show gamification progress for default tenant */}
                      {profile?.xp_points !== undefined && tenantSlug === 'default' && (
                        <div className="mt-2 pt-2 border-t border-border">
                          <p className="text-xs text-muted-foreground mb-1">Progress</p>
                          <p className="text-sm font-bold text-amber-600">Level {profile?.level || 1} • {profile?.xp_points || 0} XP</p>
                          {profile?.prestige_level > 0 && (
                            <div className="mt-2">
                              <PrestigeBadge prestigeLevel={profile.prestige_level} size="xs" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      {isAdminUser && (
                        <button
                          onClick={() => { navigate(`/app/admin${tenantQuery}`); setIsProfileOpen(false); }}
                          className="w-full px-4 py-2 text-sm text-foreground hover:bg-accent/50 transition-colors flex items-center gap-2"
                        >
                          <Icon name="ShieldCheck" size={16} />
                          Admin
                        </button>
                      )}
                      {hasCoachingPlan && hasUGCPlan && (
                        <button
                          onClick={handleProductSwitch}
                          className="w-full px-4 py-2 text-sm text-foreground hover:bg-accent/50 transition-colors flex items-center gap-2"
                        >
                          <Icon name="Shuffle" size={16} />
                          Switch product
                        </button>
                      )}
                      <button
                        onClick={() => toggleTheme()}
                        className="w-full px-4 py-2 text-sm text-foreground hover:bg-accent/50 transition-colors flex items-center gap-2"
                      >
                        <Icon name="Palette" size={16} />
                        Toggle Theme
                      </button>
                      <button
                        onClick={handleSettingsClick}
                        className="w-full px-4 py-2 text-sm text-foreground hover:bg-accent/50 transition-colors flex items-center gap-2"
                      >
                        <Icon name="Settings" size={16} />
                        Settings
                      </button>
                      <button
                        onClick={handleSignOut}
                        className="w-full px-4 py-2 text-sm text-error hover:bg-error/10 transition-colors flex items-center gap-2 border-t border-border"
                      >
                        <Icon name="LogOut" size={16} />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Mobile menu drawer */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-2 bg-card border border-border rounded-lg shadow-lg p-3 space-y-2">
            {mobileMenuItems.map((item, idx) => (
              <button
                key={`${item.path}-${idx}`}
                onClick={() => {
                  handleNavClick(item);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground hover:bg-accent"
              >
                {item.icon && <Icon name={item.icon} size={16} />}
                <span>{item.label}</span>
              </button>
            ))}
            <div className="border-t border-border pt-2">
              <button
                onClick={() => { handleSettingsClick(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground hover:bg-accent"
              >
                <Icon name="Settings" size={16} />
                Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavigationBar;
